import React from 'react'
import { useCallback, useEffect, useState, useRef, useMemo, FC } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import styles from './style.module.scss'
import * as calendarService from '../../modules/CalendarService'
import * as dateUtils from '../../utils/DateUtils'
import WeekHeader from '../WeekHeader'
import PeriodView from '../PeriodView'
import TimeLineView from '../TimeLineView'
import DayEventList from '../DayEventList'
import WeekAllday from '../WeekAllday'
import TimezoneNameBlock from '../TimezoneNameBlock'

import {
  getTodoInstances,
  addTodoChangeListener,
  removeTodoChangeListener,
} from '../../modules/TodoUtils'

const UPDATE_INTERVAL = 25000 // 현재 시간 업데이트 간격 (25초)
const SLOT_HEIGHT = 34

interface RootState {
  settings: any
}

interface WeekBoxOwnProps {
  date: Date
  onEditEvent: (
    date: Date,
    instanceId?: string | null,
    callback?: (() => void) | null,
    targetDates?: any,
    weekly?: boolean,
    isAllday?: boolean,
    onCancel?: () => void,
  ) => void
  onEventClick: (event: any, cellElement: HTMLDivElement | null) => void
  onEventDoubleClick: (event: any, cellElement: HTMLDivElement | null) => void
  dayLength?: number
}

const mapStateToProps = (state: RootState) => ({
  settings: state.settings,
})

const connector = connect(mapStateToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type WeekBoxProps = PropsFromRedux & WeekBoxOwnProps

const WeekBox: FC<WeekBoxProps> = ({
  date,
  onEditEvent,
  onEventClick,
  onEventDoubleClick,
  settings,
  dayLength = 7,
}) => {
  // 주의 시작일로부터 7일 배열 생성 - useMemo로 변경하여 불필요한 재계산 방지
  const firstWeekday = settings.calendar.firstdayOfWeek

  const weekDates = useMemo(() => {
    let firstDate = null
    const zeroDate = dateUtils.getDateZero(date)
    if (dayLength === 7) {
      firstDate = dateUtils.getWeekFirstDate(zeroDate)
    } else {
      firstDate = dateUtils.dateAddingDate(zeroDate, -1)
    }
    return Array.from({ length: dayLength }, (_, i) => {
      return dateUtils.dateAddingDate(firstDate, i)
    })
  }, [date, firstWeekday])

  const [weekEvents, setWeekEvents] = useState([])
  const [weekAllday, setWeekAllday] = useState([])
  const [currentTime, setCurrentTime] = useState(new Date())
  const [todayIndex, setTodayIndex] = useState(-1) // 오늘 날짜의 인덱스

  const weekBoxRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const headerBlockRef = useRef(null) // headerBlock에 대한 ref 추가
  const resizeObserverRef = useRef(null)
  const timerRef = useRef(null)
  const timeLineContainerRef = useRef(null)
  const resizeTimerRef = useRef(null)

  // DayEventList 관련 상태 추가
  const [showEventsList, setShowEventsList] = useState(false)
  const [eventsListPosition, setEventsListPosition] = useState({ top: 0, left: 0 })
  const [selectedDayIndex, setSelectedDayIndex] = useState(0)

  // 현재 시간 위치 계산
  const getCurrentTimePosition = useCallback(() => {
    const hours = currentTime.getHours()
    const minutes = currentTime.getMinutes()
    const seconds = currentTime.getSeconds()

    // 분 단위로 변환
    const totalMinutes = hours * 60 + minutes + seconds / 60

    // 30분 슬롯 기준으로 위치 계산 (1슬롯 = 30px)
    return (totalMinutes / 30) * SLOT_HEIGHT
  }, [currentTime])

  // 오늘 날짜 인덱스 계산 - 별도의 함수로 분리
  const findTodayIndex = useCallback(() => {
    const today = new Date()
    const todayDateStr = today.toDateString()
    return weekDates.findIndex((date) => date.toDateString() === todayDateStr)
  }, [weekDates])

  // 현재 시간 업데이트 함수
  const updateCurrentTime = useCallback(() => {
    const now = new Date()
    setCurrentTime(now)
  }, [])

  // 초기 스크롤 위치 설정 함수
  const setInitialScrollPosition = useCallback(() => {
    // 오늘 날짜가 현재 주에 포함되어 있는지 확인
    const newTodayIndex = findTodayIndex()

    if (todayIndex !== newTodayIndex) {
      setTodayIndex(newTodayIndex)
    }

    if (newTodayIndex !== -1 && scrollContainerRef.current) {
      const position = getCurrentTimePosition() - 100 // 현재 시간 위치에서 약간 위로 스크롤
      if (position > 0) {
        scrollContainerRef.current.scrollTop = position
      }
    } else if (scrollContainerRef.current) {
      // 오늘이 아닌 경우 9시 위치로 스크롤 (9시간 * 60px = 540px)
      scrollContainerRef.current.scrollTop = 540
    }
  }, [])

  // 스크롤바 너비 계산 함수 추가
  const calculateScrollbarWidth = useCallback(() => {
    if (scrollContainerRef.current && headerBlockRef.current) {
      // 스크롤바가 보이는지 확인
      const hasScrollbar =
        scrollContainerRef.current.scrollHeight > scrollContainerRef.current.clientHeight

      if (hasScrollbar) {
        // 스크롤바 너비 계산 (offsetWidth - clientWidth)
        const width =
          scrollContainerRef.current.offsetWidth - scrollContainerRef.current.clientWidth

        // headerBlock 스타일 조정
        headerBlockRef.current.style.width = `calc(100% - ${width}px)`
      } else {
        headerBlockRef.current.style.width = '100%'
      }
    }
  }, [])

  // 이벤트 계산 함수
  const calculateEvents = useCallback(async () => {
    // 언마운트되었거나 DOM에서 분리된(detached) 인스턴스는 계산/렌더를 건너뛴다.
    // 라우터 전환 등으로 남은 유령 인스턴스가 emit마다 setState를 호출해
    // 실제 보이는 인스턴스의 렌더를 방해하는 것을 방지한다.
    if (!weekBoxRef.current?.isConnected) {
      return
    }

    // 캘린더 서비스에서 이벤트 가져오기
    const instances = calendarService.getInstances()

    // 각 날짜별 이벤트 필터링
    const newWeekEvents = weekDates.map((date) => {
      const dayStart = new Date(date)
      dayStart.setHours(0, 0, 0, 0)

      const dayEnd = new Date(date)
      dayEnd.setHours(23, 59, 59, 999)

      return instances.filter(
        (instance) =>
          !instance.allday &&
          instance.startDate < dayEnd &&
          instance.endDate.valueOf() - 1 >= dayStart.valueOf(),
      )
    })

    const weekStartDate = weekDates[0]
    const weekEndDate = dateUtils.dateAddingDate(weekStartDate, dayLength)
    // 각 날짜별 종일 이벤트 필터링
    const newWeekAllday = instances.filter((instance) => {
      const endTime =
        instance.endDate.valueOf() -
        (instance.endDate.valueOf() === instance.startDate.valueOf() ? 0 : 1)
      return (
        instance.allday && instance.startDate < weekEndDate && endTime >= weekStartDate.valueOf()
      )
    })

    const todoInstances = await getTodoInstances(weekStartDate, weekEndDate)
    if (todoInstances.length > 0) {
      newWeekAllday.push(...todoInstances)
      const locale = settings.general?.locale
      newWeekAllday.sort((a, b) => {
        if (a.startDate.getTime() !== b.startDate.getTime()) {
          return a.startDate.getTime() - b.startDate.getTime()
        }
        if (a.endDate.getTime() !== b.endDate.getTime()) {
          return b.endDate.getTime() - a.endDate.getTime()
        }

        if (a.todo && !b.todo) {
          return 1
        }
        if (b.todo && !a.todo) {
          return 1
        }
        if (a.todo?.status === 'COMPLETED') {
          return 1
        }
        if (b.todo?.status === 'COMPLETED') {
          return -1
        }
        const aSubject = a.subject || ''
        const bSubject = b.subject || ''
        return aSubject.localeCompare(bSubject, locale)
      })
    }

    // await(getTodoInstances) 사이에 언마운트/detach된 경우, 죽은 인스턴스가
    // setState를 호출하지 않도록 한 번 더 확인한다.
    if (!weekBoxRef.current?.isConnected) {
      return
    }
    setWeekEvents(newWeekEvents)
    setWeekAllday(newWeekAllday)
  }, [weekDates])

  // 컴포넌트 마운트 시 오늘 날짜 인덱스 설정
  useEffect(() => {
    const newTodayIndex = findTodayIndex()
    setTodayIndex(newTodayIndex)
  }, [findTodayIndex])

  // 컴포넌트 마운트/언마운트 및 날짜 변경 시 이벤트 계산
  useEffect(() => {
    calculateEvents()

    // 이벤트 리스너 등록
    calendarService.addInstanceListener(calculateEvents)
    addTodoChangeListener(calculateEvents)

    // 리사이즈 옵저버 설정
    if (weekBoxRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        if (resizeTimerRef.current != null) {
          cancelAnimationFrame(resizeTimerRef.current)
        }
        resizeTimerRef.current = requestAnimationFrame(() => {
          calculateEvents()
          calculateScrollbarWidth() // 리사이즈 시 스크롤바 너비 재계산
          resizeTimerRef.current = null
        })
      })

      resizeObserverRef.current.observe(weekBoxRef.current)
    }

    return () => {
      // 이벤트 리스너 제거
      calendarService.removeInstanceListener(calculateEvents)
      removeTodoChangeListener(calculateEvents)

      // 리사이즈 옵저버 제거
      if (resizeObserverRef.current && weekBoxRef.current) {
        resizeObserverRef.current.disconnect()
      }
      if (resizeTimerRef.current != null) {
        cancelAnimationFrame(resizeTimerRef.current)
      }
    }
  }, [calculateEvents, calculateScrollbarWidth])

  // 현재 시간 업데이트를 위한 별도의 useEffect
  useEffect(() => {
    // 초기 업데이트
    updateCurrentTime()

    // 타이머 설정
    timerRef.current = setInterval(updateCurrentTime, UPDATE_INTERVAL)

    return () => {
      // 타이머 제거
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [updateCurrentTime])

  // 스크롤 위치 설정을 위한 별도의 useEffect
  useEffect(() => {
    // 컴포넌트가 마운트된 후 스크롤 위치 설정
    setInitialScrollPosition()
  }, [])

  useEffect(() => {
    // 초기 스크롤바 너비 계산
    calculateScrollbarWidth()

    // 스크롤 이벤트 리스너 추가
    const handleScroll = () => {
      calculateScrollbarWidth()
    }

    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [calculateScrollbarWidth])

  useEffect(() => {
    addDraggingEvent()
    return () => {
      removeDraggingEvent()
    }
  }, [date])

  // 이벤트 클릭 핸들러
  const handleEventClick = (event, cellElement, dayIndex) => {
    if (event.isOverCount) {
      // 더보기 클릭 시 DayEventList 표시
      const containerRect = weekBoxRef.current.getBoundingClientRect()
      const rect = cellElement.getBoundingClientRect()
      if (rect && containerRect) {
        setEventsListPosition({
          top: 20,
          left: rect.left - containerRect.left - (dayIndex === 6 ? 60 : 40),
        })
      }
      setSelectedDayIndex(dayIndex)
      setShowEventsList(true)
    } else {
      onEventClick(event, cellElement)
    }
  }

  const handleEventDoubleClick = (event, cellElement) => {
    onEventDoubleClick(event, cellElement)
  }

  // DayEventList 닫기 핸들러
  const handleCloseEventsList = () => {
    setShowEventsList(false)
  }

  // DayEventList 렌더링 완료 핸들러
  const handleEventsListDidRender = (element) => {
    if (element) {
      const elementRect = element.getBoundingClientRect()

      // 화면 밖으로 나가지 않도록 위치 조정
      let top = 20
      let left = element.left

      if (left + elementRect.width > window.innerWidth) {
        left = window.innerWidth - elementRect.width - 10
      }

      setEventsListPosition({ top, left })
    }
  }

  const getAlldayEventList = (dayIndex) => {
    const dayStart = new Date(weekDates[dayIndex])
    dayStart.setHours(0, 0, 0, 0)

    const dayEnd = new Date(weekDates[dayIndex])
    dayEnd.setHours(23, 59, 59, 999)

    return weekAllday.filter((instance) => {
      const endTime =
        instance.endDate.valueOf() -
        (instance.endDate.valueOf() === instance.startDate.valueOf() ? 0 : 1)
      return instance.startDate < dayEnd && endTime >= dayStart.valueOf()
    })
  }

  //New event를 위한 Dragging 처리.
  const isDragging = useRef(false)
  const isStreching = useRef(false)
  const startPosition = useRef({ x: 0, y: 0 })
  const endPosition = useRef(null)
  const [newEventArea, setNewEventArea] = useState([])
  const [headerNewEventArea, setHeaderNewEventArea] = useState(null)

  const calcPoint = (e) => {
    const bound = timeLineContainerRef.current.getBoundingClientRect()

    const parentX = bound.left
    const parentY = bound.top
    const x = e.clientX - parentX
    const y = e.clientY - parentY
    return { x: x, y: y }
  }

  const calcTime = ({ x, y }, forEnd = false) => {
    const bound = timeLineContainerRef.current.getBoundingClientRect()
    const colWidth = bound.width / dayLength
    const colIndex = Math.floor(x / colWidth)
    let inDate = dateUtils.getWeekFirstDate(date)
    inDate = dateUtils.dateAddingDate(inDate, colIndex)
    inDate = dateUtils.getDateZero(inDate)

    let minute = null
    if (forEnd) {
      minute = Math.ceil(y / SLOT_HEIGHT) * 30
    } else {
      minute = Math.floor(y / SLOT_HEIGHT) * 30
    }
    if (minute < 0) {
      minute = 0
    }
    inDate.setMinutes(minute)

    return inDate
  }

  const calcPosFromDate = (date) => {
    const bound = timeLineContainerRef.current.getBoundingClientRect()

    const colWidth = bound.width / dayLength
    const colIndex = weekDates.findIndex(
      (d) =>
        d.getFullYear() === date.getFullYear() &&
        d.getMonth() === date.getMonth() &&
        d.getDate() === date.getDate(),
    )
    const y = ((date.getHours() * 60 + date.getMinutes()) / 30) * SLOT_HEIGHT
    return { x: colIndex * colWidth, y: y }
  }

  const calcEventArea = (startPos, endPos, strechInfo = null) => {
    const eventArea = []
    const bound = timeLineContainerRef.current.getBoundingClientRect()
    const colWidth = bound.width / dayLength
    let startIndex = Math.floor(startPos.x / colWidth)
    let endIndex = Math.floor(endPos.x / colWidth)
    let startY = startPos.y
    let endY = endPos.y

    if (startIndex > endIndex) {
      const temp = startIndex
      startIndex = endIndex
      endIndex = temp
      const tempY = startY
      startY = endY
      endY = tempY
    } else if (startIndex === endIndex) {
      if (startY > endY) {
        const temp = startY
        startY = endY
        endY = temp
      }
    }

    startY = Math.floor(startY / SLOT_HEIGHT) * SLOT_HEIGHT
    endY = Math.ceil(endY / SLOT_HEIGHT) * SLOT_HEIGHT

    if (startIndex === endIndex) {
      eventArea.push({
        x: startIndex * colWidth,
        y: startY,
        width: colWidth,
        height: endY - startY,
      })
    } else {
      const containerHeight = SLOT_HEIGHT * 2 * 24
      let index = startIndex
      while (index <= endIndex) {
        if (index === startIndex) {
          eventArea.push({
            x: index * colWidth,
            y: startY,
            width: colWidth,
            height: containerHeight - startY,
          })
        } else if (index === endIndex) {
          eventArea.push({
            x: index * colWidth,
            y: 0,
            width: colWidth,
            height: endY,
          })
        } else {
          eventArea.push({
            x: index * colWidth,
            y: 0,
            width: colWidth,
            height: containerHeight,
          })
        }
        index++
      }
    }

    if (strechInfo) {
      if (strechInfo.isTop) {
        eventArea[0].strechInfo = strechInfo
      } else {
        eventArea[eventArea.length - 1].strechInfo = strechInfo
      }
    }

    setNewEventArea(eventArea)
    setHeaderNewEventArea(null)
    console.log('event dates', eventArea)
  }

  const onEventStrechStart = (event, isTop, _, e) => {
    console.log('onEventStrechStart', event, isTop)
    if (e.buttons === 1) {
      isStreching.current = true
      startPosition.current = calcPoint(e)
      console.log('startPosition', startPosition.current)
    }
  }

  const onEventStrechMove = (event, isTop, _, e) => {
    // console.log('onEventStrechMove', event, isTop, e)
    if (e.buttons !== 1 || !isStreching.current) {
      return
    }

    const pos = calcPoint(e)
    if (pos === null) {
      return
    }

    const bound = timeLineContainerRef.current.getBoundingClientRect()
    if (pos.x <= 0) {
      pos.x = 1
    } else if (pos.x >= bound.width) {
      pos.x = bound.width - 1
    }

    console.log('onEventStrechMove', pos)

    let topTime = null
    let bottomTime = null

    if (isTop) {
      topTime = calcTime(pos)
      bottomTime = event.endDate
      if (topTime >= bottomTime) {
        topTime = new Date(bottomTime.getTime() - 1000 * 60 * 30)
      }
    } else {
      topTime = event.startDate
      bottomTime = calcTime(pos, true)
      if (topTime >= bottomTime) {
        bottomTime = new Date(topTime.getTime() + 1000 * 60 * 30)
      }
    }

    const startPos = calcPosFromDate(topTime)
    const endPos = calcPosFromDate(bottomTime)

    calcEventArea(startPos, endPos, { isTop: isTop })
  }

  const onEventStrechEnd = (event, isTop, _, e, isCancel = false) => {
    console.log('onEventStrechEnd', event, isTop, e, isCancel)
    if (!isCancel) {
      const pos = calcPoint(e)
      if (pos === null) {
        _completeStrech()
        return
      }

      const bound = timeLineContainerRef.current.getBoundingClientRect()
      const pBound = timeLineContainerRef.current.parentElement.getBoundingClientRect()
      let visibleArea = {
        x: 0,
        y: pBound.top - bound.top,
        width: pBound.width,
        height: pBound.height,
      }

      const x = pos.x
      const y = pos.y
      if (
        x < 0 ||
        x > visibleArea.width ||
        y < visibleArea.y ||
        y > visibleArea.y + visibleArea.height
      ) {
        _completeStrech()
        return
      }

      let topTime = null
      let bottomTime = null

      if (isTop) {
        topTime = calcTime(pos)
        bottomTime = event.endDate
        if (topTime >= bottomTime) {
          topTime = new Date(bottomTime.getTime() - 1000 * 60 * 30)
        }
      } else {
        topTime = event.startDate
        bottomTime = calcTime(pos, true)
        if (topTime >= bottomTime) {
          bottomTime = new Date(topTime.getTime() + 1000 * 60 * 30)
        }
      }

      const instanceId = event.instanceId
      const instance = calendarService.getInstance(instanceId)
      if (!instance) {
        console.log('instance not found', instanceId)
        _completeStrech()
        return
      }
      calendarService.updateEventChangeTime(instance, topTime, bottomTime, false, (success) => {
        if (success) {
          console.log('update event success', instanceId)
        } else {
          console.log('update event failed', instanceId)
        }
        _completeStrech()
      })
    } else {
      _completeStrech()
    }
  }

  const _completeStrech = () => {
    isStreching.current = false
    startPosition.current = null
    endPosition.current = null

    setNewEventArea([])
  }

  const onMouseDown = (e) => {
    if (e.buttons === 1) {
      isDragging.current = true
      startPosition.current = calcPoint(e)
      console.log('startPosition', startPosition.current)
    }
  }

  const onMouseMove = (e) => {
    if (e.buttons === 1 && isDragging.current) {
      document.body.style.userSelect = 'none'
      const pos = calcPoint(e)
      if (pos === null) {
        return
      }

      const startPos = startPosition.current
      if (endPosition.current === null) {
        const offset = 15
        if (Math.abs(pos.x - startPos.x) > offset || Math.abs(pos.y - startPos.y) > offset) {
          endPosition.current = pos
        } else {
          return
        }
      } else {
        endPosition.current = pos
      }

      calcEventArea(startPos, pos)
    }
  }

  const onDragEnd = (e) => {
    console.log('onDragEnd', e)

    document.body.style.userSelect = 'auto'
    setNewEventArea([])
    setHeaderNewEventArea(null)
  }

  const addDraggingEvent = () => {
    window.addEventListener('mouseup', onMouseUp)
    window.addEventListener('dragend', onDragEnd)
  }

  const removeDraggingEvent = () => {
    window.removeEventListener('mouseup', onMouseUp)
    window.removeEventListener('dragend', onDragEnd)
  }

  const onMouseUp = (e) => {
    if (isDragging.current) {
      console.log('onMouseUp', e)
      isDragging.current = false

      const pos = calcPoint(e)
      if (pos === null || endPosition.current === null) {
        setNewEventArea([])
        setHeaderNewEventArea(null)
        return
      }

      const time1 = calcTime(startPosition.current)
      const time2 = calcTime(pos, true)
      let startDate = time1
      let endDate = time2
      if (time2 <= time1) {
        startDate = time2
        if (!(startDate.getHours() === 0 && startDate.getMinutes() === 0)) {
          startDate = new Date(startDate.getTime() - 1000 * 60 * 30)
        }
        endDate = time1
        if (!(endDate.getHours() === 0 && endDate.getMinutes() === 0)) {
          endDate = new Date(endDate.getTime() + 1000 * 60 * 30)
        }
      }

      onEditEvent(date, null, null, { start: startDate, end: endDate }, false, false, () => {
        endPosition.current = null
        setNewEventArea([])
        setHeaderNewEventArea(null)
      })

      console.log('startDate', startDate)
      console.log('endDate', endDate)

      //TODO: Calc Time from startPosition and endPosition.

      //TODO: New event를 위한 처리. 좌표 계산.
      // MouseDown과 MouseMove 마지막의 좌표를 시간으로 변환하고 둘중에 작은 값을 시작시간,
      // 큰 값을 종료시간으로 해서 Event Editor를 Present.
      // 시작시간
    }
    document.body.style.userSelect = 'auto'
    setNewEventArea([])
    setHeaderNewEventArea(null)
  }

  const handleDrop = (dropEvent, toAllday) => {
    console.log('handleDrop', dropEvent, toAllday)
    const e = dropEvent

    const instanceId = e.dataTransfer.getData('data')
    console.log('onDrop', e, instanceId)

    const pos = calcPoint(e)
    if (pos === null) {
      setNewEventArea([])
      setHeaderNewEventArea(null)
      return
    }

    const instance = calendarService.getInstance(instanceId)
    if (!instance) {
      console.log('instance not found', instanceId)
      setNewEventArea([])
      setHeaderNewEventArea(null)
      return
    }

    const toTime = calcTime(pos)
    let newEndTime = null

    if (!toAllday) {
      let duration = instance.endDate.getTime() - instance.startDate.getTime()
      if (instance.allday) {
        duration = settings.calendar.defaultDuration * 60 * 1000
      }

      newEndTime = new Date(toTime.getTime() + duration)
    } else {
      toTime.setHours(0, 0, 0, 0)

      let duration = 1 * 24 * 60 * 60 * 1000
      const diff =
        dateUtils.betweenDay(
          instance.startDate,
          instance.endDate.getTime() === instance.startDate.getTime()
            ? instance.endDate
            : new Date(instance.endDate.getTime()),
        ) + 1
      if (diff !== 0) {
        duration = (diff + (instance.allday ? 0 : 1)) * 24 * 60 * 60 * 1000
      }

      newEndTime = new Date(toTime.getTime() + duration)
    }

    calendarService.updateEventChangeTime(instance, toTime, newEndTime, toAllday, (success) => {
      if (success) {
        console.log('update event success', instanceId)
      } else {
        console.log('update event failed', instanceId)
      }
    })
    setNewEventArea([])
    setHeaderNewEventArea(null)
  }

  const draggingEventDate = useRef({ start: null, end: null, fromAllday: false })

  const handleDragStart = (e, fromAllday = false) => {
    console.log('handleDragStart', e, fromAllday)
    const instanceId = e.dataTransfer.getData('data')
    const instance = calendarService.getInstance(instanceId)
    if (!instance) {
      console.log('instance not found', instanceId)
      draggingEventDate.current = { start: null, end: null, fromAllday: false }
      return
    }
    draggingEventDate.current = {
      start: instance.startDate,
      end: instance.endDate,
      fromAllday: fromAllday,
    }
  }

  const handleDragMove = (e) => {
    console.log('handleDragMove', e)

    let isAllday = false

    const bound = timeLineContainerRef.current.getBoundingClientRect()
    const pBound = timeLineContainerRef.current.parentElement.getBoundingClientRect()
    let visibleArea = {
      x: 0,
      y: pBound.top - bound.top,
      width: pBound.width,
      height: pBound.height,
    }

    const parentX = bound.left
    const parentY = bound.top
    const x = e.clientX - parentX
    const y = e.clientY - parentY
    if (
      x < 0 ||
      x > visibleArea.width ||
      y < visibleArea.y ||
      y > visibleArea.y + visibleArea.height
    ) {
      isAllday = true
    }

    const pos = calcPoint(e)
    if (pos === null) {
      setNewEventArea([])
      setHeaderNewEventArea(null)
      return
    }

    const colWidth = bound.width / dayLength

    let startIndex = Math.floor(pos.x / colWidth)
    startIndex = startIndex < 0 ? 0 : startIndex
    startIndex = startIndex > 6 ? 6 : startIndex
    const draggingDate = draggingEventDate.current

    if (isAllday) {
      let diff = 1
      if (draggingDate.start !== null && draggingDate.end !== null) {
        const start = new Date(draggingDate.start)
        const end = new Date(
          draggingDate.end.getTime() === start.getTime()
            ? draggingDate.end
            : draggingDate.end.getTime() - 1,
        )
        diff = dateUtils.betweenDay(start, end) + 1
      }

      const headerBound = headerBlockRef.current.getBoundingClientRect()
      const height = headerBound.height

      setHeaderNewEventArea({
        x: colWidth * startIndex + 95, //95 is empty cell width
        y: 0,
        width: colWidth * diff,
        height: height,
      })
      setNewEventArea([])
    } else {
      const startY = Math.floor(pos.y / SLOT_HEIGHT) * SLOT_HEIGHT
      const _endY = Math.ceil(pos.y / SLOT_HEIGHT) * SLOT_HEIGHT

      const duration = draggingDate.fromAllday
        ? settings.calendar.defaultDuration * 60 * 1000
        : draggingDate.end.getTime() - draggingDate.start.getTime()
      const slotCount = Math.ceil(duration / 1000 / 60 / 30)
      const containerHeight = SLOT_HEIGHT * 2 * 24
      let remainSlotHeight = SLOT_HEIGHT * slotCount

      const newEventAreas = []
      let posY = startY
      let slotHeight = remainSlotHeight

      if (slotHeight + startY > containerHeight) {
        slotHeight = containerHeight - startY
      }

      do {
        newEventAreas.push({
          x: colWidth * startIndex,
          y: posY,
          width: colWidth,
          height: slotHeight,
        })
        posY = 0
        startIndex++
        remainSlotHeight -= slotHeight
        slotHeight = remainSlotHeight > containerHeight ? containerHeight : remainSlotHeight
      } while (slotHeight > 0)

      setNewEventArea(newEventAreas)
      setHeaderNewEventArea(null)
    }
  }

  const handleDoubleClick = (e, date, isAllday = false) => {
    console.log('handleDoubleClick', isAllday, date)
    let targetDates = null
    if (!isAllday) {
      const duration = settings.calendar.defaultDuration * 60 * 1000
      targetDates = {
        start: date,
        end: new Date(date.getTime() + duration),
      }
    }
    onEditEvent(date, null, null, targetDates, false, isAllday, () => {
      setNewEventArea([])
      setHeaderNewEventArea(null)
    })
  }

  const handleAlldayDoubleClick = (e, date) => {
    handleDoubleClick(e, date, true)
  }

  return (
    <div className={styles.weekBox} ref={weekBoxRef}>
      {/* 상단 블록 (Empty, Week Headers, Allday Containers) */}
      <div className={styles.headerBlock} ref={headerBlockRef}>
        {/* Timezone Name 셀 */}
        <TimezoneNameBlock targetDate={weekDates[0]} />

        {/* 요일 헤더 컨테이너 */}
        <div
          className={styles.headerContent}
          onDrop={(e) => {
            handleDrop(e, true)
          }}
          onDragOver={(e) => {
            e.preventDefault()
          }}
          onDragStart={(e) => {
            handleDragStart(e, true)
          }}
          onDrag={(e) => {
            handleDragMove(e)
          }}
        >
          <div className={styles.weekHeaderRow}>
            {weekDates.map((date, index) => (
              <div key={`header-${index}`} className={styles.dayColumn}>
                <WeekHeader
                  date={date}
                  isToday={index === todayIndex}
                  onDoubleClick={handleAlldayDoubleClick}
                />
              </div>
            ))}
          </div>

          {/* 종일 이벤트 컨테이너 */}
          <div className={styles.alldayRow}>
            <WeekAllday
              weekStartDate={weekDates[0]}
              dayLength={dayLength}
              instances={weekAllday}
              onEventClick={(event, cell, index) => handleEventClick(event, cell, index)}
              onEventDoubleClick={(event, cell) => handleEventDoubleClick(event, cell)}
              onDoubleClick={handleAlldayDoubleClick}
              onChangeStrechArea={(strechAreaIndexes, isLeft, posY) => {
                console.log('onChangeStrechArea', strechAreaIndexes)
                if (strechAreaIndexes === null) {
                  isStreching.current = false
                  setHeaderNewEventArea(null)
                  return
                }

                const bound = headerBlockRef.current.getBoundingClientRect()
                const colWidth = (bound.width - 95) / dayLength
                const x = strechAreaIndexes[0] * colWidth + 95 //95 is empty cell width
                const y = posY - bound.y
                const width = colWidth * strechAreaIndexes.length
                const height = 20

                setHeaderNewEventArea({
                  x: x,
                  y: y,
                  width: width,
                  height: height,
                  strechInfo: {
                    isLeft: isLeft,
                  },
                })
              }}
            />
          </div>
          {headerNewEventArea && (
            <div
              className={styles.newEvent}
              style={{
                top: `${headerNewEventArea.y}px`,
                left: `${headerNewEventArea.x}px`,
                width: `${headerNewEventArea.width}px`,
                height: `${headerNewEventArea.height}px`,
              }}
            >
              {headerNewEventArea.strechInfo && (
                <div
                  className={`${styles.strechHandle} ${
                    headerNewEventArea.strechInfo.isLeft
                      ? styles.strechHandleLeft
                      : styles.strechHandleRight
                  }`}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* 하단 컨테이너 (Period, Timeline) */}
      <div className={styles.contentContainer}>
        {/* 스크롤 컨테이너 추가 */}
        <div className={styles.scrollContainer} ref={scrollContainerRef}>
          {/* 왼쪽 시간대 표시 영역 */}
          <PeriodView targetDate={weekDates[0]} />

          {/* 타임라인 컨테이너 */}
          <div
            className={styles.timelineContainer}
            draggable={false}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onDrop={(e) => {
              handleDrop(e, false)
            }}
            onDragOver={(e) => {
              e.preventDefault()
            }}
            onDragStart={(e) => {
              handleDragStart(e, false)
            }}
            onDrag={(e) => {
              handleDragMove(e)
            }}
            ref={timeLineContainerRef}
          >
            {weekDates.map((date, index) => (
              <div
                key={`timeline-${index}`}
                id={`timeline-${index}`}
                className={styles.timelineDayColumn}
              >
                <TimeLineView
                  showTimeMark={true}
                  timeline={weekEvents[index] || []}
                  date={date}
                  onStrechStart={onEventStrechStart}
                  onStrechMove={onEventStrechMove}
                  onStrechEnd={onEventStrechEnd}
                  onEventClick={(event, cell) => {
                    onEventClick(event, cell)
                    setNewEventArea([])
                  }}
                  onDoubleClick={(e, date) => {
                    handleDoubleClick(e, date, false)
                  }}
                  onEventDoubleClick={onEventDoubleClick}
                />
              </div>
            ))}

            {newEventArea.map((area, index) => (
              <div
                key={`newEvent-${index}`}
                className={styles.newEvent}
                style={{
                  position: 'absolute',
                  top: `${area.y}px`,
                  left: `${area.x}px`,
                  width: `${area.width}px`,
                  height: `${area.height}px`,
                }}
              >
                {area.strechInfo && (
                  <div
                    className={`${styles.strechHandle} ${
                      area.strechInfo.isTop ? styles.strechHandleTop : styles.strechHandleBottom
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 이벤트 목록 모달 */}
      {showEventsList && (
        <>
          <div className={styles.overlay} onClick={() => setShowEventsList(false)} />
          <div
            className={styles.eventsListContainer}
            style={{
              position: 'absolute',
              top: `${eventsListPosition.top}px`,
              left: `${eventsListPosition.left}px`,
            }}
          >
            <DayEventList
              date={weekDates[selectedDayIndex]}
              instances={getAlldayEventList(selectedDayIndex)}
              onClose={handleCloseEventsList}
              onDidRender={handleEventsListDidRender}
              onCellClick={onEventClick}
              onCellDoubleClick={onEventDoubleClick}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default connector(WeekBox)
